<template>
    <div class="report-reason text-left">
        <label :for="kind + '-reason'"><strong>{{ kind === 'delay' ? 'Почему отчёт не подан вовремя?' : 'Почему нет фотографий смены?' }}</strong></label>
        <b-form-select :id="kind + '-reason'" :value="reasonKey" :options="options" @input="$emit('update:reasonKey', $event)" />
        <div class="report-reason__reserved d-sm-none small mt-2">
            <strong v-for="item in choices" :key="item.key" :class="{'report-reason__hidden': item.key !== reasonKey}" :aria-hidden="item.key !== reasonKey ? 'true' : null">{{ item.label }}</strong>
        </div>
        <p class="small text-muted mt-2">Причина и уточнение попадут в отчёт и Telegram-группу объекта от вашего имени.</p>
        <label :for="kind + '-detail'"><strong>Ваше уточнение</strong></label>
        <div class="report-reason__reserved small mb-2" :id="kind + '-help'">
            <span :class="{'report-reason__hidden': selected}" :aria-hidden="selected ? 'true' : null">Сначала выберите причину, затем поясните обстоятельства своими словами.</span>
            <span v-for="item in choices" :key="item.key" :class="{'report-reason__hidden': item.key !== reasonKey}" :aria-hidden="item.key !== reasonKey ? 'true' : null">{{ item.help }}</span>
        </div>
        <b-dropdown ref="historyMenu" class="report-reason__history mb-2" menu-class="report-reason-history-menu" block
            variant="outline-secondary" text="Выбрать из прошлых смен" :disabled="!historyReady">
            <template #button-content>
                Выбрать из прошлых смен <span v-if="suggestions.length" class="report-reason__count">{{ suggestions.length }}</span>
            </template>
            <b-dropdown-text v-if="suggestionsLoading">Загружаем уточнения…</b-dropdown-text>
            <b-dropdown-text v-else-if="suggestionsError">{{ suggestionsError }}</b-dropdown-text>
            <b-dropdown-text v-else-if="!suggestions.length">Для этого объекта и причины прошлых уточнений пока нет.</b-dropdown-text>
            <b-dropdown-item-button v-for="item in suggestions" :key="item.shift_id" @click="applySuggestion(item)">
                <span class="small text-muted">Смена {{ shiftDate(item.shift_start) }}</span>
                <blockquote class="user-quote my-1">{{ item.detail }}</blockquote>
                <span class="small text-primary">Использовать это уточнение</span>
            </b-dropdown-item-button>
        </b-dropdown>
        <AppTextarea :id="kind + '-detail'" class="report-reason__detail" :value="detail" maxlength="4000"
            :aria-describedby="kind + '-help ' + kind + '-feedback'" @input="onDetailInput" @blur="touched = true" />
        <b-button v-if="undoDetail !== null" size="sm" variant="link" class="px-0 mt-1" @click="undoSuggestion">Отменить подстановку</b-button>
        <div v-if="suggestionsError" role="alert" class="small text-danger mt-2">
            {{ suggestionsError }}
            <b-button size="sm" variant="outline-secondary" @click="loadSuggestions">Повторить загрузку уточнений</b-button>
        </div>
        <div :id="kind + '-feedback'" role="alert" class="text-danger mt-2">
            <template v-if="issue && (touched || detail || reasonKey)">{{ issue.message }} <span v-if="!selected || issue.help !== selected.help">{{ issue.help }}</span></template>
        </div>
        <div class="report-reason__policy mt-3" role="note" :aria-labelledby="kind + '-policy-title'">
            <strong :id="kind + '-policy-title'"><span class="report-reason__policy-icon" aria-hidden="true">i</span> Пояснение должно содержать факты</strong>
            <p>Опишите конкретные обстоятельства в деловом тоне. Точки и тире вместо ответа и бессмысленные отписки не заменяют пояснение причины.</p>
            <p>Формальное заполнение поля оставляет руководство без необходимых фактов, мешает принимать обоснованные управленческие решения, снижает доверие к автору отчёта и вредит его профессиональной репутации.</p>
        </div>
        <div v-if="selected && detail && !issue" class="report-reason__preview small mt-3">
            <strong>Так будет выглядеть ваше объяснение:</strong>
            <blockquote class="user-quote mt-2">{{ preview }}</blockquote>
        </div>
    </div>
</template>
<script>
import explanations from '../../../../shared/reportExplanations';
import api from '@/store/ShiftReport/api/index';
export default {
    name: 'ReportExplanationField',
    props: {kind: String, reasonKey: String, detail: String},
    data: () => ({touched: false, undoDetail: null, suggestions: [], suggestionsLoading: false, suggestionsError: '',
        historyRequest: 0}),
    computed: {
        choices() { return explanations.choices(this.kind); },
        options() { return [{value: '', text: 'Выберите причину'}, ...this.choices.map(item => ({value: item.key, text: item.label}))]; },
        selected() { return explanations.choice(this.kind, this.reasonKey); },
        issue() { return explanations.validateExplanation(this.kind, this.reasonKey, this.detail); },
        preview() { return explanations.formatExplanation(this.kind, this.reasonKey, this.detail); },
        reportState() { return this.$store.state.shiftReport || {}; },
        identity() {
            const user = this.$store.getters.currentUser || {};
            return user.company && (user.userId || user.id) ? JSON.stringify([user.company, user.userId || user.id]) : '';
        },
        historyContext() {
            const state = this.reportState;
            return JSON.stringify([this.identity, state.selectedProcess, state.startDate, state.startTime, this.kind, this.reasonKey]);
        },
        historyReady() {
            const state = this.reportState;
            return Boolean(this.identity && state.selectedProcess && this.selected && Number.isFinite(explanations.shiftStart(state.startDate, state.startTime)));
        }
    },
    watch: {
        historyContext: {immediate: true, handler() { this.undoDetail = null; this.loadSuggestions(); }}
    },
    beforeDestroy() { this.historyRequest++; },
    methods: {
        shiftDate(value) { const date = String(value || '').slice(0, 10); return date.split('-').reverse().join('.'); },
        onDetailInput(value) { this.undoDetail = null; this.$emit('update:detail', value); },
        applySuggestion(item) {
            this.undoDetail = this.detail || '';
            this.$emit('update:detail', item.detail);
            if (this.$refs.historyMenu) this.$refs.historyMenu.hide(true);
            this.$nextTick(() => { const input = this.$el.querySelector('textarea'); if (input) input.focus(); });
        },
        undoSuggestion() { this.$emit('update:detail', this.undoDetail); this.undoDetail = null; },
        async loadSuggestions() {
            const request = ++this.historyRequest, context = this.historyContext;
            this.suggestions = []; this.suggestionsError = ''; this.suggestionsLoading = false;
            if (!this.historyReady) return;
            this.suggestionsLoading = true;
            const state = this.reportState;
            try {
                const result = await api.getExplanationSuggestions({building_process_id: state.selectedProcess,
                    kind: this.kind, reason_key: this.reasonKey, start_date: state.startDate, start_time: state.startTime});
                if (request === this.historyRequest && context === this.historyContext) this.suggestions = result.items || [];
            } catch (error) {
                if (request === this.historyRequest && context === this.historyContext) this.suggestionsError = 'Не удалось загрузить уточнения. Можно продолжить ввод вручную.';
            } finally {
                if (request === this.historyRequest && context === this.historyContext) this.suggestionsLoading = false;
            }
        }
    }
};
</script>
<style scoped>
.report-reason { width: 100%; max-width: 640px; min-width: 0; margin: 0 auto; }
.report-reason__reserved { display: grid; }
.report-reason__reserved > * { grid-area: 1 / 1; min-width: 0; }
.report-reason__hidden { visibility: hidden; }
.report-reason__detail { resize: vertical; overflow-y: auto; min-height: 8.75rem; }
.report-reason__policy { padding: 12px 14px; border: 1px solid #d9b96c; border-radius: 10px; background: #fffcf5; color: #4f4635; font-size: .9rem; line-height: 1.5; overflow-wrap: anywhere; }
.report-reason__policy > strong { display: block; margin-bottom: 8px; color: #69501b; }
.report-reason__policy p { margin: 0 0 8px; }
.report-reason__policy p:last-child { margin-bottom: 0; }
.report-reason__policy-icon { display: inline-flex; align-items: center; justify-content: center; width: 1.15em; height: 1.15em; border: 1px solid currentColor; border-radius: 50%; font-size: .85em; font-family: Georgia, serif; }
.report-reason__history { width: 100%; }
.report-reason__count { display: inline-block; border-radius: 12px; min-width: 1.5em; padding: 0 .3em; background: #e8f0f5; color: #334f61; font-size: .8em; }
.report-reason__preview blockquote { max-height: 10rem; overflow-y: auto; margin-bottom: 0; }
.report-reason__history ::v-deep .report-reason-history-menu { width: 100%; min-width: 0; max-height: 320px; overflow-y: auto; }
.report-reason__history ::v-deep .dropdown-item { white-space: normal; overflow-wrap: anywhere; padding: 10px 12px; }
.report-reason__history ::v-deep .b-dropdown-text { min-width: 0; white-space: normal; }
.user-quote { white-space: pre-wrap; overflow-wrap: anywhere; }
</style>
